import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Large Projects · master ON/OFF switch (admin dashboard).
//
// This is the SINGLE recovery control for the Large Projects vertical. The flag
// SiteSettings.largeProjectsEnabled gates the entire LP section (companies,
// projects, quotes, escrow). When OFF, all the Big Projects sub-pages AND the
// BigProjectsMetrics dashboard widget self-hide — so this toggle MUST:
//   1) always render (even OFF, even if the read errors), and
//   2) use only the UNGATED ops, otherwise there'd be no way to flip it back ON.
//
// Ungated ops (proven live, forwarded through the /lp-graphql proxy via lpApi):
//   READ : query { largeProjectsEnabled { status largeProjectsEnabled } }
//   FLIP : mutation { setLargeProjectsEnabled(enabled: Boolean!) {
//            status largeProjectsEnabled errorMessage } }
// The admin's id_token cookie carries auth through the proxy (requireAdmin).
//
// SSR-safe: no `window` at module load; window.confirm only inside the handler.
// Mirrors the run()/toast pattern from bigProjects/CompanyProviders.js.
// ============================================================================

const Q_STATUS = `
  query {
    largeProjectsEnabled {
      status
      largeProjectsEnabled
    }
  }
`;

const M_SET = `
  mutation ($enabled: Boolean!) {
    setLargeProjectsEnabled(enabled: $enabled) {
      status
      largeProjectsEnabled
      errorMessage
    }
  }
`;

const S = {
  card: {
    border: '1px solid #e7e9ec',
    borderRadius: 14,
    padding: '20px 22px',
    background: '#fff',
    boxShadow: '0 6px 18px rgba(15,18,20,0.03)',
    margin: '24px 0 0',
    fontFamily: "'Inter', -apple-system, sans-serif",
    color: '#0f1214',
  },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 },
  titleWrap: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  title: { fontSize: 18, fontWeight: 800, margin: 0 },
  pill: (on, unknown) => ({
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.05em',
    padding: '4px 12px',
    borderRadius: 999,
    background: unknown ? '#eef2f4' : on ? '#e7f6ec' : '#fde7e8',
    color: unknown ? '#5a6168' : on ? '#137a37' : '#c2151c',
    whiteSpace: 'nowrap',
  }),
  subtitle: { color: '#6b7280', fontSize: 13.5, marginTop: 8, lineHeight: 1.5, maxWidth: 640 },
  // Toggle switch
  switchBtn: (on, busy) => ({
    position: 'relative',
    width: 58,
    height: 32,
    borderRadius: 999,
    border: 'none',
    cursor: busy ? 'wait' : 'pointer',
    background: on ? '#16a34a' : '#cbd2d9',
    transition: 'background 160ms ease',
    flexShrink: 0,
    opacity: busy ? 0.7 : 1,
    padding: 0,
  }),
  knob: (on) => ({
    position: 'absolute',
    top: 3,
    left: on ? 29 : 3,
    width: 26,
    height: 26,
    borderRadius: '50%',
    background: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
    transition: 'left 160ms ease',
  }),
  alert: (ok) => ({
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13.5,
    marginTop: 14,
    background: ok ? '#ecfdf5' : '#fef2f2',
    color: ok ? '#065f46' : '#991b1b',
  }),
};

function LargeProjectsToggle() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [unknown, setUnknown] = useState(false); // read failed → state unknown
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await lpApi(Q_STATUS);
      const env = res && res.data && res.data.largeProjectsEnabled;
      if (env && env.status === 200) {
        setEnabled(!!env.largeProjectsEnabled);
        setUnknown(false);
      } else {
        // Read failed — still render the card so the admin can recover.
        setUnknown(true);
      }
    } catch (e) {
      setUnknown(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const flip = async () => {
    if (busy) return;
    const next = !enabled;
    setMsg(null);
    // Confirm ONLY when turning ON (going live for everyone on this env).
    if (next && typeof window !== 'undefined') {
      const ok = window.confirm(
        'Turn Large Projects ON? This makes the entire Large Projects section live for everyone on this environment.'
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const res = await lpApi(M_SET, { enabled: next });
      if (res.errors) { setMsg({ error: firstError(res) }); setBusy(false); return; }
      const env = res.data && res.data.setLargeProjectsEnabled;
      if (env && env.status >= 200 && env.status < 300) {
        setEnabled(!!env.largeProjectsEnabled);
        setUnknown(false);
        setMsg({ ok: env.largeProjectsEnabled
          ? 'Large Projects is now ON — the section is live on this environment.'
          : 'Large Projects is now OFF — the section is hidden and inert. Small jobs are unaffected.' });
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Action failed: ${e.message}` });
    }
    setBusy(false);
  };

  const pillText = loading ? 'CHECKING…' : unknown ? 'UNKNOWN' : enabled ? 'ON' : 'OFF';

  return (
    <div style={S.card}>
      <div style={S.rowTop}>
        <div>
          <div style={S.titleWrap}>
            <h3 style={S.title}>Large Projects</h3>
            <span style={S.pill(enabled, loading || unknown)}>{pillText}</span>
          </div>
          <div style={S.subtitle}>
            Master switch for the entire Large Projects vertical (companies, projects, quotes, escrow).
            OFF = invisible &amp; inert; small jobs unaffected.
            {unknown ? ' Status could not be read — you can still flip the switch to recover.' : ''}
          </div>
        </div>
        <button
          type="button"
          aria-pressed={enabled}
          aria-label={enabled ? 'Turn Large Projects off' : 'Turn Large Projects on'}
          style={S.switchBtn(enabled, busy || loading)}
          disabled={busy || loading}
          onClick={flip}
        >
          <span style={S.knob(enabled)} />
        </button>
      </div>
      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}
    </div>
  );
}

export default LargeProjectsToggle;
